import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../shared/services/auth.service';
import { DataInitService } from '../shared/services/data-init.service';
import { DatabaseService } from '../shared/services/database.service';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('loginVideo') loginVideoRef!: ElementRef<HTMLVideoElement>;
  loginForm!: FormGroup;
  errorMessage = '';
  isLoading = false;

  /** Solo en modo DEMO: mostrar modal de bienvenida cuando la base no está inicializada */
  isDemoMode = environment.demo;
  /** En demo: false hasta comprobar si la base está inicializada (evita parpadeo del formulario) */
  demoCheckDone = !environment.demo;
  showDemoWelcome = false;
  showDemoCredentials = false;
  demoInitInProgress = false;
  private initCheckInterval: ReturnType<typeof setInterval> | null = null;

  readonly DEMO_USER = 'admin';
  readonly DEMO_PASSWORD = 'admin123';

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private dataInitService: DataInitService,
    private database: DatabaseService
  ) {}

  ngOnInit(): void {
    // Si ya está autenticado, redirigir al dashboard
    if (this.authService.isAuthenticated()) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.loginForm = this.fb.group({
      username: ['', [Validators.required, Validators.minLength(3)]],
      password: ['', [Validators.required, Validators.minLength(6)]]
    });

    if (this.isDemoMode) {
      this.database.isInitialized().subscribe(initialized => {
        this.demoCheckDone = true;
        if (!initialized) {
          this.showDemoWelcome = true;
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.playBackgroundVideo();
  }

  private playBackgroundVideo(): void {
    const video = this.loginVideoRef?.nativeElement;
    if (!video) return;
    // Asegurar audio desactivado antes de cualquier reproducción (evita sonido al cerrar sesión o entrar al login)
    video.muted = true;
    video.volume = 0;
    video.setAttribute('muted', 'true');
    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        video.play().catch(() => {});
      });
    }
  }

  ngOnDestroy(): void {
    if (this.initCheckInterval) {
      clearInterval(this.initCheckInterval);
    }
  }

  acceptDemoWelcome(): void {
    this.showDemoWelcome = false;
    this.demoInitInProgress = true;
    this.dataInitService.initializeTestData();

    const maxAttempts = 25;
    let attempts = 0;
    this.initCheckInterval = setInterval(() => {
      attempts++;
      this.database.isInitialized().subscribe(initialized => {
        if (initialized || attempts >= maxAttempts) {
          if (this.initCheckInterval) {
            clearInterval(this.initCheckInterval);
            this.initCheckInterval = null;
          }
          this.demoInitInProgress = false;
          this.showDemoCredentials = true;
        }
      });
    }, 800);
  }

  closeDemoCredentials(): void {
    this.showDemoCredentials = false;
    this.loginForm.patchValue({
      username: this.DEMO_USER,
      password: this.DEMO_PASSWORD
    });
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const { username, password } = this.loginForm.value;

      this.authService.login(username, password).subscribe({
        next: (success) => {
          this.isLoading = false;
          if (success) {
            this.router.navigate(['/dashboard']);
          } else {
            this.errorMessage = 'Usuario o contraseña incorrectos';
          }
        },
        error: () => {
          this.isLoading = false;
          this.errorMessage = 'Error al iniciar sesión. Intente nuevamente.';
        }
      });
    } else {
      this.errorMessage = 'Por favor, complete todos los campos';
    }
  }
}
